import React, { useState, useEffect } from 'react';
import { Trash2, Edit, Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import api from '../api/axios';
import moment from 'moment-timezone';
import Table from '../components/Table';

export function CompaniesPage() {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    systemId: '',
    location: '',
    webhookUrl: '',
    telegramNotificationsChatId: '',
    telegramNotificationsChatLink: '',
    eventRedirectUrl: '',
    notifyViaTelegram: false,
    notifyOnCheckIn: false,
    notifyOnCheckOut: false,
    weekends: [6, 7],
    defaultWorkingHours: 8,
    penaltyPerLateMinute: 0,
    subscription: {
      plan: 'Monthly',
      price: 0,
      isActive: false,
      expiresAt: '',
    }
  });

  const [editingCompanyId, setEditingCompanyId] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        const res = await api.get('/companies');
        if (isMounted) setCompanies(res.data?.data || []);
      } catch (err) {
        if (isMounted) setError(err.response?.data?.message || t('failedLoadCompanies'));
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchCompanies();
    return () => { isMounted = false; };
  }, []);

  const generateRandomId = (length = 10) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('subscription.')) {
      const key = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        subscription: {
          ...prev.subscription,
          [key]: type === 'checkbox' ? checked : value
        }
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleExpiresAtChange = (date) => {
    if (!date) {
      setFormData(prev => ({
        ...prev,
        subscription: {
          ...prev.subscription,
          expiresAt: ''
        }
      }));
      return;
    }

    // Store as UTC ISO string (consistent with your backend expectation)
    const utcIso = moment(date).startOf('day').utc().toISOString();
    setFormData(prev => ({
      ...prev,
      subscription: {
        ...prev.subscription,
        expiresAt: utcIso
      }
    }));
  };

  const getExpiresAtForDatePicker = () => {
    if (!formData.subscription.expiresAt) return null;
    // Convert stored UTC ISO back to local date object for datepicker
    return moment(formData.subscription.expiresAt).toDate();
  };

  const openEditModal = (company) => {
    setFormData({
      name: company.name || '',
      systemId: company.systemId || '',
      location: company.location || '',
      webhookUrl: company.webhookUrl || '',
      notifyViaTelegram: !!company.notifyViaTelegram,
      notifyOnCheckIn: !!company.notifyOnCheckIn,
      notifyOnCheckOut: !!company.notifyOnCheckOut,
      weekends: company.weekends || [6, 7],
      telegramNotificationsChatId: company.telegramNotificationsChatId || '',
      telegramNotificationsChatLink: company.telegramNotificationsChatLink || '',
      eventRedirectUrl: company.eventRedirectUrl || '',
      defaultWorkingHours: company.defaultWorkingHours ?? 8,
      penaltyPerLateMinute: company.penaltyPerLateMinute ?? 0,
      subscription: {
        plan: company.subscription?.plan || 'Monthly',
        price: company.subscription?.price ?? 0,
        isActive: !!company.subscription?.isActive,
        expiresAt: company.subscription?.expiresAt || '',
      }
    });
    setEditingCompanyId(company._id);
    setIsModalOpen(true);
  };

  const handleCreateNew = () => {
    const systemId = generateRandomId(20);
    setFormData({
      name: '',
      systemId,
      location: '',
      webhookUrl: `https://tranzact.uz/events/${systemId}`,
      notifyViaTelegram: false,
      notifyOnCheckIn: false,
      notifyOnCheckOut: false,
      telegramNotificationsChatId: '',
      eventRedirectUrl: '',
      telegramNotificationsChatLink: '',
      weekends: [6, 7],
      defaultWorkingHours: 8,
      penaltyPerLateMinute: 0,
      subscription: {
        plan: 'Monthly',
        price: 0,
        isActive: false,
        expiresAt: '',
      }
    });
    setEditingCompanyId(null);
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (editingCompanyId) {
        const res = await api.put(`/companies/${editingCompanyId}`, formData);
        setCompanies((prev) => prev.map(c => c._id === editingCompanyId ? res.data.data : c));
      } else {
        const res = await api.post('/companies', formData);
        setCompanies((prev) => [...prev, res.data.data]);
      }
      setIsModalOpen(false);
    } catch (err) {
      alert(err.response?.data?.message || t('failedToSaveCompany'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (companyId) => {
    if (!window.confirm(t('deleteCompanyConfirm'))) return;
    try {
      await api.delete(`/companies/${companyId}`);
      setCompanies(companies.filter(c => c._id !== companyId));
    } catch (err) {
      alert(err.response?.data?.message || t('failedDeleteCompany'));
    }
  };

  if (loading) return <div className="p-10 text-center text-gray-300">Loading...</div>;
  if (error) return <div className="p-10 text-center text-red-400">{error}</div>;

  return (
    <div className="p-6 text-white">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-blue-400">{t('companies')}</h1>
        <button
          onClick={handleCreateNew}
          className="flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded-xl shadow-lg shadow-sky-900/20 transition-all duration-200 active:scale-95 font-medium"
        >
          <Plus size={18} /> {t('createNewCompany')}
        </button>
      </div>

      <div className="overflow-x-auto bg-gray-800 rounded-2xl">
        <Table
          data={companies}
          columns={[
            { header: t('name'), accessor: 'name', sortable: true, className: 'font-medium' },
            { header: t('id'), sortAccessor: 'systemId', sortable: true, render: (company) => company.systemId || '—' },
            { header: t('location'), sortAccessor: 'location', sortable: true, render: (company) => company.location || '—' },
            { header: t('webhook'), sortAccessor: 'webhookUrl', className: 'break-all', render: (company) => company.webhookUrl || `https://tranzact.uz/events/${company.systemId}` },
            { 
              header: 'Subscription', 
              sortAccessor: 'subscription.plan',
              render: (company) => (
                <>
                  {company.subscription?.plan || '—'}<br />
                  {company.subscription?.expiresAt
                    ? new Date(company.subscription.expiresAt).toLocaleString()
                    : '—'}
                </>
              )
            },
            {
              header: t('actions'),
              align: 'center',
              render: (company) => (
                <div className="flex justify-center gap-4">
                  <button onClick={() => openEditModal(company)} className="text-blue-400 hover:text-blue-300">
                    <Edit size={18} />
                  </button>
                  <button onClick={() => handleDelete(company._id)} className="text-red-500 hover:text-red-400">
                    <Trash2 size={18} />
                  </button>
                </div>
              )
            }
          ]}
          headerClassName="bg-gray-700 text-gray-300"
          rowClassName="border-t border-gray-700 hover:bg-gray-900/50 transition"
          keyExtractor={(company) => company._id}
        />
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-2xl shadow-2xl relative">
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-6 text-blue-400">
              {editingCompanyId ? t('editCompany') : t('createNewCompany')}
            </h2>

            <div className="grid grid-cols-2 gap-4">
              {Object.keys(formData)
                .filter(k => k !== 'subscription')
                .map((key) => (
                  <div key={key} className="col-span-2 md:col-span-1">
                    <label className="block text-sm text-gray-300 mb-2">{key}</label>
                    {typeof formData[key] === 'boolean' ? (
                      <input
                        type="checkbox"
                        name={key}
                        checked={formData[key]}
                        onChange={handleInputChange}
                        className="h-5 w-5"
                      />
                    ) : (
                      <input
                        type={typeof formData[key] === 'number' ? 'number' : 'text'}
                        name={key}
                        value={formData[key]}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                      />
                    )}
                  </div>
                ))}

              <div className="col-span-2 mt-4 border-t border-gray-700 pt-4">
                <h3 className="text-lg text-blue-300 mb-4">Subscription</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Plan</label>
                    <select
                      name="subscription.plan"
                      value={formData.subscription.plan}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg"
                    >
                      <option>Monthly</option>
                      <option>Quarterly</option>
                      <option>Semi-Annual</option>
                      <option>Annual</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Price</label>
                    <input
                      type="number"
                      name="subscription.price"
                      value={formData.subscription.price}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Expires At</label>
                    <DatePicker
                      selected={getExpiresAtForDatePicker()}
                      onChange={handleExpiresAtChange}
                      dateFormat="yyyy-MM-dd"
                      placeholderText="Select date"
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                      calendarClassName="bg-gray-800 border-gray-700 text-white"
                      dayClassName={() => "text-white"}
                      popperClassName="!z-50"
                      showPopperArrow={false}
                    />
                  </div>

                  <div className="flex items-center gap-2 mt-6">
                    <input
                      type="checkbox"
                      name="subscription.isActive"
                      checked={formData.subscription.isActive}
                      onChange={handleInputChange}
                    />
                    <label className="text-sm text-gray-300">Active</label>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={handleSave}
              className="mt-8 w-full bg-blue-600 hover:bg-blue-700 py-3 rounded-xl font-semibold transition"
              disabled={saving}
            >
              {saving ? t('saving') : t('save')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}